import React, { useEffect, useRef, useState } from 'react';
import { faFileImport } from '@fortawesome/free-solid-svg-icons';
import {
    DragDropFile,
    FileListViewer,
    LoadingView,
    Modal,
} from '@quyva/shared-ui';
import { ImportLayoutView } from './importLayoutView';
import { ImportTemplateView } from './importTemplateView';
import { getFileFormat } from '@quyva/common';
import { isMobile } from 'react-device-detect';

export const AllowedFileExtensions = Object.freeze({
    CSV: 'csv',
    PY: 'py',
    ZIP: 'zip',
});

const ImportType = Object.freeze({
    TEMPLATE: 'template',
    LAYOUT: 'layout',
});

export const ImportTemplateOrLayout = ({
    user,
    setViewImportModal,
    fetchTemplateList,
    setActiveTemplate,
    setSelectedTemplate,
    setSelectedLayout,
    templateList,
    setTemplateList,
    handleLoadTemplate,
    getLayoutsList,
    setAddNewTemplateMode,
}) => {
    const [importType, setImportType] = useState(ImportType.TEMPLATE);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    const fileInputRef = useRef(null);

    useEffect(() => {
        setError('');

        if (selectedFiles && selectedFiles.length) {
            if (selectedFiles.length > 1) {
                setError('Only single file is accepted!');
                setImportType(null);
            } else if (selectedFiles.length === 1) {
                const fileExtension = getFileFormat(selectedFiles[0].name);
                if (
                    !Object.values(AllowedFileExtensions).includes(
                        fileExtension
                    )
                ) {
                    setError(
                        `Only these file extensions are allowed: ${Object.values(
                            AllowedFileExtensions
                        ).join(', ')}`
                    );
                    setImportType(null);
                } else {
                    if (
                        fileExtension === AllowedFileExtensions.CSV ||
                        fileExtension === AllowedFileExtensions.PY
                    ) {
                        setImportType(ImportType.TEMPLATE);
                    } else {
                        setImportType(ImportType.LAYOUT);
                    }
                }
            }
        } else {
            // Programmatically set the files of the file input to null
            if (fileInputRef.current) {
                const dataTransfer = new DataTransfer();
                fileInputRef.current.files = dataTransfer.files;
            }
        }
    }, [selectedFiles]);

    // Handle file selection
    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setSelectedFiles(files);
    };

    // Handle file drop
    const handleFileDrop = (event) => {
        event.preventDefault();
        const files = Array.from(event.dataTransfer.files);
        setSelectedFiles(files);

        // Programmatically set the files of the file input
        if (fileInputRef.current) {
            const dataTransfer = new DataTransfer();
            files.forEach((file) => dataTransfer.items.add(file));
            fileInputRef.current.files = dataTransfer.files;
        }
    };

    // Prevent default behavior for file drop
    const handleDragOver = (event) => {
        event.preventDefault();
    };

    return (
        <Modal
            title={'Import Template / Layout'}
            titleIcon={faFileImport}
            iconColor={'sky'}
            onClose={() => setViewImportModal(false)}
            className={''}
        >
            <div className='w-[60vw] min-w-[500px] flex-col px-6 py-6 rounded-xl bg-backgrounds-slate text-texts-secondary300'>
                {isLoading ? (
                    <div className='w-full h-32 flex justify-center items-center w-full h-full py-6 my-1.5'>
                        <LoadingView loadingText={loadingText} inLine={false} />
                    </div>
                ) : (
                    <div>
                        <input
                            ref={fileInputRef}
                            type='file'
                            accept='.zip,.csv,.py'
                            className='bg-backgrounds-white w-full p-2 rounded-xl border cursor-pointer'
                            onChange={handleFileSelect}
                            single
                            style={{
                                maxWidth: '80rem', // or a specific width like '20rem' based on your layout
                                textOverflow: 'ellipsis', // adds ellipsis to overflowing text
                                overflow: 'hidden', // hides overflowing text
                            }}
                        />

                        {!isMobile && (
                            <DragDropFile
                                onDrop={(e) => {
                                    const files = e.dataTransfer.files;
                                    if (files.length > 0) {
                                        handleFileDrop(e); // Proceed with the file drop
                                    }
                                }}
                                onDragOver={handleDragOver}
                                text={
                                    'Drag and drop file here or click on choose file icon to Import file'
                                }
                            />
                        )}

                        <FileListViewer selectedFiles={selectedFiles} />

                        {importType === ImportType.TEMPLATE ? (
                            <ImportTemplateView
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                templateList={templateList}
                                setTemplateList={setTemplateList}
                                error={error}
                                setError={setError}
                                fetchTemplateList={fetchTemplateList}
                                setActiveTemplate={setActiveTemplate}
                                setViewImportModal={setViewImportModal}
                                user={user}
                                setLoadingText={setLoadingText}
                                setIsLoading={setIsLoading}
                            />
                        ) : (
                            <ImportLayoutView
                                selectedFiles={selectedFiles}
                                setSelectedFiles={setSelectedFiles}
                                error={error}
                                setError={setError}
                                setViewImportModal={setViewImportModal}
                                user={user}
                                setLoadingText={setLoadingText}
                                setIsLoading={setIsLoading}
                                fetchTemplateList={fetchTemplateList}
                                setTemplateList={setTemplateList}
                                templateList={templateList}
                                handleLoadTemplate={handleLoadTemplate}
                                getLayoutsList={getLayoutsList}
                                setSelectedTemplate={setSelectedTemplate}
                                setSelectedLayout={setSelectedLayout}
                                setAddNewTemplateMode={setAddNewTemplateMode}
                            />
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};
